import { supabase } from '../lib/supabase'
import type { MeseSerie } from './scostamento'

export type StatoFonte = 'attivo' | 'credenziali_mancanti' | 'non_configurato'

export const ETICHETTE_STATO: Record<StatoFonte, string> = {
  attivo: 'Attivo',
  credenziali_mancanti: 'Credenziali mancanti',
  non_configurato: 'Non configurato',
}

export interface ImpiantoGreen {
  readonly id: number
  readonly impiantoId: number
  readonly ragioneSociale: string
  readonly codiceImpianto: string
  readonly comune: string
  readonly kwp: string | null
  readonly gestoreRete: string | null
  readonly statoDistributore: StatoFonte
  readonly ultimoMeseDistributore: string | null
  readonly statoGse: StatoFonte
  readonly ultimoScaricamentoGse: string | null
  /** Null: vale la soglia del partner. */
  readonly soglia: string | null
  readonly serie: readonly MeseSerie[]
}

export interface MeseCompleto extends MeseSerie {
  readonly immissione: string | null
  readonly autoconsumo: string | null
  readonly prelievo: string | null
}

/** PostgREST annida la relazione come oggetto o come array secondo i tipi: si accettano entrambe. */
type Annidato<T> = T | T[] | null
const uno = <T,>(v: Annidato<T>): T | null => (Array.isArray(v) ? (v[0] ?? null) : v)
// numeric arriva come number da PostgREST: si torna a stringa prima di ogni calcolo.
const testo = (v: number | string | null) => (v === null ? null : String(v))

interface RigaImpianto {
  codice_impianto: string | null
  codice_ditta: string
  ubicazione: { comune?: string } | null
  azienda: Annidato<{ ragione_sociale: string }>
}

interface RigaGreen {
  id: number
  impianto_id: number
  kwp: number | string | null
  gestore_rete: string | null
  stato_distributore: StatoFonte
  ultimo_mese_distributore: string | null
  stato_gse: StatoFonte
  ultimo_scaricamento_gse: string | null
  soglia_scostamento: number | string | null
  impianto: Annidato<RigaImpianto>
  serie_mensile: { anno: number; mese: number; produzione: number | string | null }[]
}

const anagrafica = (i: RigaImpianto | null) => ({
  ragioneSociale: uno(i?.azienda ?? null)?.ragione_sociale ?? '',
  codiceImpianto: i?.codice_impianto ?? i?.codice_ditta ?? '',
  comune: i?.ubicazione?.comune ?? '',
})

// ponytail: la serie di produzione arriva annidata in un'unica chiamata; con migliaia di
// impianti conviene una vista che calcoli gli ultimi 24 mesi lato database.
export async function elencaImpiantiGreen(): Promise<ImpiantoGreen[]> {
  const { data, error } = await supabase
    .from('impianto_green')
    .select(
      'id, impianto_id, kwp, gestore_rete, stato_distributore, ultimo_mese_distributore, stato_gse, ultimo_scaricamento_gse, soglia_scostamento, impianto(codice_impianto, codice_ditta, ubicazione, azienda(ragione_sociale)), serie_mensile(anno, mese, produzione)',
    )
    .order('id')
  if (error) throw new Error(error.message)
  return ((data ?? []) as unknown as RigaGreen[]).map((r) => ({
    id: r.id,
    impiantoId: r.impianto_id,
    ...anagrafica(uno(r.impianto)),
    kwp: testo(r.kwp),
    gestoreRete: r.gestore_rete,
    statoDistributore: r.stato_distributore,
    ultimoMeseDistributore: r.ultimo_mese_distributore,
    statoGse: r.stato_gse,
    ultimoScaricamentoGse: r.ultimo_scaricamento_gse,
    soglia: testo(r.soglia_scostamento),
    serie: r.serie_mensile.map((m) => ({ anno: m.anno, mese: m.mese, produzione: testo(m.produzione) })),
  }))
}

export async function serieImpianto(idGreen: number): Promise<MeseCompleto[]> {
  const { data, error } = await supabase
    .from('serie_mensile')
    .select('anno, mese, produzione, immissione, autoconsumo, prelievo')
    .eq('impianto_green_id', idGreen)
    .order('anno')
    .order('mese')
  if (error) throw new Error(error.message)
  return (data ?? []).map((m) => ({
    anno: m.anno,
    mese: m.mese,
    produzione: testo(m.produzione),
    immissione: testo(m.immissione),
    autoconsumo: testo(m.autoconsumo),
    prelievo: testo(m.prelievo),
  }))
}

export async function caricaSogliaPartner(idPartner: number): Promise<string> {
  const { data, error } = await supabase.from('partner').select('soglia_scostamento').eq('id', idPartner).single()
  if (error) throw new Error(error.message)
  return String(data.soglia_scostamento)
}

export async function salvaSogliaPartner(idPartner: number, soglia: string): Promise<void> {
  const { error } = await supabase.from('partner').update({ soglia_scostamento: soglia }).eq('id', idPartner)
  if (error) throw new Error(error.message)
}

export async function salvaSogliaImpianto(idGreen: number, soglia: string | null): Promise<void> {
  const { error } = await supabase.from('impianto_green').update({ soglia_scostamento: soglia }).eq('id', idGreen)
  if (error) throw new Error(error.message)
}

export interface ImpiantoDaAggiungere {
  readonly impiantoId: number
  readonly ragioneSociale: string
  readonly codiceImpianto: string
  readonly comune: string
}

export async function impiantiDaAggiungere(): Promise<ImpiantoDaAggiungere[]> {
  const { data, error } = await supabase
    .from('impianto')
    .select('id, codice_impianto, codice_ditta, ubicazione, azienda(ragione_sociale), impianto_green!left(id)')
    .is('impianto_green', null)
    .eq('stato', 'attivo')
    .order('id')
  if (error) throw new Error(error.message)
  return ((data ?? []) as unknown as (RigaImpianto & { id: number })[]).map((r) => ({
    impiantoId: r.id,
    ...anagrafica(r),
  }))
}

export async function aggiungiImpiantoGreen(impiantoId: number, kwp: string, gestoreRete: string): Promise<void> {
  const { error } = await supabase
    .from('impianto_green')
    .insert({ impianto_id: impiantoId, kwp: kwp === '' ? null : kwp, gestore_rete: gestoreRete || null })
  if (error) throw new Error(error.message)
}
